import { useMutation, useQueryClient } from "@tanstack/react-query";
import { postRequest } from "@/utils/apiHelper";
import { toast } from "sonner";
import { MailMessage } from "./use-messages";

/**
 * Hook to mark messages as read/unread with optimistic updates
 */
export function useMarkRead() {
  const queryClient = useQueryClient();

  const markAsReadMutation = useMutation({
    mutationFn: async (messageId: string) => {
      return await postRequest(
        `mail/messages/${messageId}/mark-read`,
        {},
        false,
        undefined,
        "default",
        undefined,
      );
    },
    onMutate: async (messageId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["mail-messages"] });

      // Snapshot previous value
      const previousQueries = queryClient.getQueriesData({
        queryKey: ["mail-messages"],
      });

      // Optimistically update all message queries
      queryClient.setQueriesData(
        { queryKey: ["mail-messages"] },
        (old: any) => {
          if (!old?.messages) return old;
          return {
            ...old,
            messages: old.messages.map((msg: MailMessage) =>
              msg.id === messageId ? { ...msg, isRead: true } : msg,
            ),
          };
        },
      );

      return { previousQueries };
    },
    onError: (err: any, variables, context) => {
      // Rollback on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error(err?.message || "Failed to mark as read");
    },
    onSuccess: () => {
      // Invalidate to sync with server
      queryClient.invalidateQueries({ queryKey: ["mail-messages"] });
    },
  });

  const markAsUnreadMutation = useMutation({
    mutationFn: async (messageId: string) => {
      return await postRequest(
        `mail/messages/${messageId}/mark-unread`,
        {},
        false,
        undefined,
        "default",
        undefined,
      );
    },
    onMutate: async (messageId) => {
      await queryClient.cancelQueries({ queryKey: ["mail-messages"] });

      const previousQueries = queryClient.getQueriesData({
        queryKey: ["mail-messages"],
      });

      queryClient.setQueriesData(
        { queryKey: ["mail-messages"] },
        (old: any) => {
          if (!old?.messages) return old;
          return {
            ...old,
            messages: old.messages.map((msg: MailMessage) =>
              msg.id === messageId ? { ...msg, isRead: false } : msg,
            ),
          };
        },
      );

      return { previousQueries };
    },
    onError: (err: any, variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error(err?.message || "Failed to mark as unread");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mail-messages"] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      return await postRequest(
        "mail/messages/mark-all-read",
        {},
        false,
        undefined,
        "default",
        undefined,
      );
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ["mail-messages"] });

      const previousQueries = queryClient.getQueriesData({
        queryKey: ["mail-messages"],
      });

      queryClient.setQueriesData(
        { queryKey: ["mail-messages"] },
        (old: any) => {
          if (!old?.messages) return old;
          return {
            ...old,
            messages: old.messages.map((msg: MailMessage) => ({ ...msg, isRead: true })),
          };
        },
      );

      return { previousQueries };
    },
    onError: (err: any, _variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error(err?.message || "Failed to mark all as read");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mail-messages"] });
    },
  });

  return {
    markAsRead: markAsReadMutation.mutate,
    markAsUnread: markAsUnreadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    isMarkingRead: markAsReadMutation.isPending,
    isMarkingUnread: markAsUnreadMutation.isPending,
    isMarkingAllRead: markAllAsReadMutation.isPending,
  };
}
